
import { useState, useEffect, useMemo } from 'react';
import { DateRange } from 'react-day-picker';
import { format, subMonths } from 'date-fns';
import { ru } from 'date-fns/locale';
import { fetchBikeRevenueData } from '@/services/analyticsService';
import { useToast } from "@/components/ui/use-toast";
import { BikeRevenueData, ChartData } from './types';

export const useBikeRevenueData = () => {
  const { toast } = useToast();
  const [date, setDate] = useState<DateRange | undefined>({
    from: subMonths(new Date(), 3),
    to: new Date(),
  });
  const [bikeData, setBikeData] = useState<BikeRevenueData[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>("all");
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [viewType, setViewType] = useState<'monthly' | 'bikes'>('monthly');
  const [targetRevenue] = useState<number>(55000); // Целевая выручка для окупаемости

  useEffect(() => {
    loadBikeRevenueData();
  }, [date]);

  const loadBikeRevenueData = async () => {
    if (!date?.from || !date?.to) return;

    setLoading(true);
    try {
      console.log(`Загрузка данных о выручке по велосипедам с ${date.from} по ${date.to}`);
      const data = await fetchBikeRevenueData(date.from, date.to);
      setBikeData(data);
      
      // Извлекаем доступные модели для фильтрации
      const models = [...new Set(data.map(bike => bike.model))];
      setAvailableModels(models);
      
      setLoading(false);
    } catch (error) {
      console.error("Ошибка при загрузке данных о выручке:", error);
      toast({
        title: "Ошибка загрузки",
        description: "Не удалось загрузить данные о выручке по велосипедам",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  // Фильтрация данных по выбранной модели
  const filteredData = useMemo(() => 
    selectedModel === "all" 
      ? bikeData
      : bikeData.filter(bike => bike.model === selectedModel)
  , [bikeData, selectedModel]);

  // Подготовка данных для графика по моделям
  const modelChartData: ChartData[] = useMemo(() => {
    const modelData: Record<string, number> = {};
    
    filteredData.forEach(bike => {
      if (!modelData[bike.model]) {
        modelData[bike.model] = 0;
      }
      modelData[bike.model] += bike.totalRevenue;
    });

    return Object.keys(modelData).map(model => ({
      name: model,
      revenue: modelData[model]
    }));
  }, [filteredData]);

  // Подготовка данных для временного графика
  const timeChartData: ChartData[] = useMemo(() => {
    if (viewType === 'monthly') {
      // Группируем по месяцам
      const monthlyData: Record<string, number> = {};
      
      filteredData.forEach(bike => {
        if (bike.monthlyData) {
          Object.entries(bike.monthlyData).forEach(([month, revenue]) => {
            if (!monthlyData[month]) {
              monthlyData[month] = 0;
            }
            monthlyData[month] += revenue;
          });
        }
      });

      return Object.keys(monthlyData)
        .sort()
        .map(month => ({
          name: month,
          revenue: monthlyData[month]
        }));
    } else {
      // Для велосипедов возвращаем топ по выручке
      return filteredData
        .sort((a, b) => b.totalRevenue - a.totalRevenue)
        .slice(0, 10)
        .map(bike => ({
          name: bike.serialNumber,
          revenue: bike.totalRevenue
        }));
    }
  }, [filteredData, viewType]);

  // Экспорт данных в CSV с информацией об окупаемости
  const exportToCSV = () => {
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "ID,Серийный номер,Модель,Выручка,Процент окупаемости,Количество аренд,Средняя выручка на аренду,Статус окупаемости\n";
    
    filteredData.forEach(bike => {
      const paybackPercentage = ((bike.totalRevenue / targetRevenue) * 100).toFixed(1);
      let status = '';
      const percentage = bike.totalRevenue / targetRevenue * 100;
      
      if (percentage < 20) status = 'Низкая доходность';
      else if (percentage < 50) status = 'Частично окупился';
      else if (percentage < 80) status = 'Приближается к окупаемости';
      else if (percentage < 100) status = 'Почти окупился';
      else status = 'Окупился полностью';
      
      csvContent += `${bike.bikeId},${bike.serialNumber},${bike.model},${bike.totalRevenue},${paybackPercentage}%,${bike.rentalCount},${bike.averageRevenue},${status}\n`;
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `bike-revenue-payback-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const dateRangeText = date?.from && date?.to 
    ? `${format(date.from, 'dd.MM.yyyy', { locale: ru })} - ${format(date.to, 'dd.MM.yyyy', { locale: ru })}`
    : 'Выберите период';

  return {
    date,
    setDate,
    bikeData,
    selectedModel,
    setSelectedModel,
    availableModels,
    loading,
    viewType,
    setViewType,
    filteredData,
    modelChartData,
    timeChartData,
    exportToCSV,
    dateRangeText,
    targetRevenue
  };
};
